import type { WorkflowContent } from "../../../api/content/authoring/processContentDecoders";
import type { DefinitionProblem } from "../../../api/content/authoring/processAuthoringApi";
import { definitionPath } from "../../processAuthoring/draft/useDefinitionCheck";

/**
 * Problems of a Workflow draft by step key (AU-6: "validation marks nodes with problems"),
 * as module B's check finds them, whose paths name a step by its position
 * (`steps.2.input_bindings…`). What a binding may name and whether an input is filled are
 * B's to say (F102 CP-04; WD-1+3 D-7), so the editor adds none of its own. Problems at no
 * step are kept apart, except those at the Workflow's own name and description, which their
 * fields show (WD-1+3 4c): they count, once.
 */
export interface EditorProblems {
  readonly byKey: ReadonlyMap<string, readonly string[]>;
  /** The bindings B refuses, as `<step key>.<slot>` (a problem at `steps.2.input_bindings.<slot>`): the inspector marks these, never by what its menu offers. */
  readonly refusedBindings: ReadonlySet<string>;
  readonly general: readonly string[];
  readonly count: number;
}

/** The Workflow's own name and description, in each language: the paths its text fields show problems at. */
const OWN_TEXTS = /^(?:name|description|translations)(?:\.|$)/;

export function editorProblems(draft: WorkflowContent, serverProblems: readonly DefinitionProblem[]): EditorProblems {
  const byKey = new Map<string, string[]>();
  const refusedBindings = new Set<string>();
  const general: string[] = [];
  const add = (key: string, message: string) => {
    const messages = byKey.get(key) ?? [];
    if (!messages.includes(message)) {
      messages.push(message);
    }
    byKey.set(key, messages);
  };
  let atTexts = 0;
  for (const problem of serverProblems) {
    const path = definitionPath(problem);
    const index = /^steps\.(\d+)(\.|$)/.exec(path)?.[1];
    const key = index === undefined ? undefined : draft.steps[Number(index)]?.key;
    if (OWN_TEXTS.test(path)) {
      atTexts += 1;
    } else if (key === undefined) {
      general.push(problem.message);
    } else {
      add(key, problem.message);
      const slot = /^steps\.\d+\.input_bindings\.([^.]+)/.exec(path)?.[1];
      if (slot !== undefined) refusedBindings.add(`${key}.${slot}`);
    }
  }
  const count = atTexts + general.length + [...byKey.values()].reduce((sum, messages) => sum + messages.length, 0);
  return { byKey, refusedBindings, general, count };
}
